// IronPact Sound Engine — optimized for phone speakers (200Hz+ frequencies)
// iOS: ctx.resume().then(() => play)

let ctx: AudioContext | null = null;
let masterVol = 0.5;
let muted = false;

export function setVolume(v: number) { masterVol = Math.max(0, Math.min(1, v)); }
export function toggleMute(): boolean { muted = !muted; return muted; }
export function isMuted(): boolean { return muted; }

function getCtx(): AudioContext {
  if (!ctx) ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  return ctx;
}

function withAudio(fn: (c: AudioContext) => void) {
  if (muted) return;
  const c = getCtx();
  c.resume().then(() => fn(c));
}

function osc(c: AudioContext, type: OscillatorType, freq: number, vol: number, dur: number, delay = 0) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(vol * masterVol, c.currentTime + delay);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + delay + dur);
  o.connect(g);
  g.connect(c.destination);
  o.start(c.currentTime + delay);
  o.stop(c.currentTime + delay + dur + 0.01);
}

export function unlockAudio() {
  const c = getCtx();
  if (c.state === "suspended") c.resume();
}

// Hex tap — short click
export function sfxTap() {
  withAudio(c => { osc(c, "sine", 600, 0.2, 0.08); });
}

// Claim hex — ascending two-tone
export function sfxClaim() {
  withAudio(c => {
    osc(c, "sine", 440, 0.25, 0.12, 0);
    osc(c, "sine", 660, 0.25, 0.15, 0.08);
  });
}

// Build — two mechanical clicks
export function sfxBuild() {
  withAudio(c => {
    osc(c, "triangle", 500, 0.25, 0.08, 0);
    osc(c, "triangle", 700, 0.25, 0.08, 0.1);
  });
}

// Train troops — snare-like
export function sfxTrain() {
  withAudio(c => {
    osc(c, "triangle", 300, 0.3, 0.06, 0);
    osc(c, "sine", 500, 0.2, 0.04, 0.03);
  });
}

// Research — rising sparkle (4 notes)
export function sfxResearch() {
  withAudio(c => {
    [523, 659, 784, 1047].forEach((f, i) => {
      osc(c, "sine", f, 0.2, 0.15, i * 0.07);
    });
  });
}

// Attack — aggressive descending
export function sfxAttack() {
  withAudio(c => {
    osc(c, "sawtooth", 500, 0.2, 0.15, 0);
    osc(c, "square", 350, 0.15, 0.1, 0.05);
    osc(c, "sawtooth", 250, 0.2, 0.2, 0.1);
  });
}

// Missile launch — rising sweep
export function sfxMissileLaunch() {
  withAudio(c => {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(300, c.currentTime);
    o.frequency.exponentialRampToValueAtTime(1200, c.currentTime + 0.3);
    g.gain.setValueAtTime(0.2 * masterVol, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.4);
    o.connect(g); g.connect(c.destination);
    o.start(c.currentTime); o.stop(c.currentTime + 0.45);
  });
}

// Missile impact — crunch
export function sfxMissileImpact() {
  withAudio(c => {
    osc(c, "square", 400, 0.3, 0.1, 0);
    osc(c, "sawtooth", 250, 0.25, 0.15, 0.05);
    osc(c, "sine", 350, 0.2, 0.2, 0.1);
  });
}

// Recruit commander — triumphant fanfare
export function sfxRecruit() {
  withAudio(c => {
    [392, 494, 587, 784].forEach((f, i) => {
      osc(c, "triangle", f, 0.2, 0.2, i * 0.1);
    });
  });
}

// Error — descending buzz
export function sfxError() {
  withAudio(c => {
    osc(c, "square", 500, 0.2, 0.08, 0);
    osc(c, "square", 350, 0.2, 0.12, 0.06);
  });
}

// Chat ping
export function sfxChat() {
  withAudio(c => { osc(c, "sine", 1000, 0.15, 0.08); });
}

// Diplomacy — horn call
export function sfxDiplomacy() {
  withAudio(c => {
    osc(c, "triangle", 400, 0.2, 0.15, 0);
    osc(c, "triangle", 533, 0.2, 0.2, 0.12);
  });
}

// Incoming alarm — urgent beeps
// Air raid siren — loops until stopSiren() is called or auto-stops after maxDuration
let sirenInterval: any = null;
let sirenTimeout: any = null;

function playSirenCycle() {
  withAudio(c => {
    const now = c.currentTime;
    const osc1 = c.createOscillator();
    const gain = c.createGain();
    osc1.type = "sawtooth";
    osc1.frequency.setValueAtTime(400, now);
    osc1.frequency.linearRampToValueAtTime(900, now + 0.8);
    osc1.frequency.linearRampToValueAtTime(400, now + 1.6);
    gain.gain.setValueAtTime(0.12, now);
    gain.gain.linearRampToValueAtTime(0.18, now + 0.8);
    gain.gain.linearRampToValueAtTime(0.12, now + 1.6);
    gain.gain.linearRampToValueAtTime(0, now + 1.8);
    osc1.connect(gain).connect(c.destination);
    osc1.start(now);
    osc1.stop(now + 1.8);
  });
}

export function sfxIncoming(durationMs?: number) {
  stopSiren();
  playSirenCycle();
  sirenInterval = setInterval(playSirenCycle, 1800);
  const maxMs = durationMs || 25000;
  sirenTimeout = setTimeout(stopSiren, maxMs);
}

export function stopSiren() {
  if (sirenInterval) { clearInterval(sirenInterval); sirenInterval = null; }
  if (sirenTimeout) { clearTimeout(sirenTimeout); sirenTimeout = null; }
}
